import { useEffect, useState } from 'react';
import { GlassCard } from '@/components/ui/GlassCard';
import { Badge } from '@/components/ui/Badge';
import { useAnswerItemIncident, useItemIncidentDetail } from '@/hooks/useItemIncident';
import {
  ApiError,
  type ItemIncidentAnswerStatus,
  type ItemIncidentDetailResponse,
} from '@/lib/api';

interface ItemIncidentDetailPanelProps {
  rowNumber: number;
}

/** 回答後に CS へ返す既定のステータス。今のステータスが選択肢に無いとき（空欄＝未着手など）に使う */
/** @graph-connects none */
const DEFAULT_STATUS: ItemIncidentAnswerStatus = 'CS確認中';

/** @graph-connects none */
function MetaItem({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </dt>
      <dd className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
        {value}
      </dd>
    </div>
  );
}

/** @graph-connects none */
function AnswerForm({ detail }: { detail: ItemIncidentDetailResponse }) {
  const { row, statusOptions, notifiesRequester, answererName } = detail;
  const initialStatus = statusOptions.find((s) => s === row.status) ?? DEFAULT_STATUS;
  const [answer, setAnswer] = useState(row.answer);
  const [status, setStatus] = useState<ItemIncidentAnswerStatus>(initialStatus);
  const mutation = useAnswerItemIncident(row.rowNumber);

  // 別の依頼に切り替わったら入力を差し替える（同じコンポーネントが使い回されるため）
  useEffect(() => {
    setAnswer(row.answer);
    setStatus(initialStatus);
  }, [row.rowNumber, row.answer, initialStatus]);

  const errorMessage =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? '保存に失敗しました。時間をおいてもう一度お試しください。'
        : null;

  return (
    <form
      aria-label="回答"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate({ answer, status });
      }}
    >
      <label
        htmlFor="item-incident-answer"
        className="text-xs font-semibold block mb-1"
        style={{ color: 'var(--color-text-muted)' }}
      >
        回答（{answererName} として記録されます）
      </label>
      <textarea
        id="item-incident-answer"
        value={answer}
        onChange={(e) => {
          setAnswer(e.target.value);
        }}
        rows={5}
        maxLength={4000}
        className="w-full rounded-md p-2 text-sm mb-2"
        style={{
          background: 'var(--color-bg-surface)',
          color: 'var(--color-text-primary)',
          border: '1px solid var(--color-border)',
        }}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <label
          htmlFor="item-incident-status"
          className="text-xs"
          style={{ color: 'var(--color-text-muted)' }}
        >
          ステータス
        </label>
        <select
          id="item-incident-status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ItemIncidentAnswerStatus);
          }}
          className="rounded-md px-2 py-1 text-sm cursor-pointer"
          style={{
            background: 'var(--color-bg-surface)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
          }}
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={mutation.isPending || answer.trim() === ''}
          className="px-3 py-1 text-sm font-medium rounded-md cursor-pointer transition-colors bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--color-primary)]"
          style={{ color: 'white' }}
        >
          {mutation.isPending ? '保存中…' : '回答を保存'}
        </button>
      </div>
      <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
        {notifiesRequester
          ? '保存すると、依頼者に Slack（元の依頼投稿のスレッド）で回答が届きます。'
          : 'この依頼はフォーム導入前のため、保存してもSlackには通知されません。'}
      </p>
      {mutation.isSuccess && (
        <p role="status" className="text-xs mt-1" style={{ color: 'var(--color-success)' }}>
          保存しました。
        </p>
      )}
      {errorMessage && (
        <p role="alert" className="text-xs mt-1" style={{ color: 'var(--color-error)' }}>
          {errorMessage}
        </p>
      )}
    </form>
  );
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アイテム確認依頼1件の詳細パネル。確認内容の全文と回答を表示し、バイヤー一覧に載っている人には回答フォームを出す
 * @graph-connects useItemIncidentDetail [calls] 依頼の詳細取得
 * @graph-connects useAnswerItemIncident [calls] 回答の送信
 */
export function ItemIncidentDetailPanel({ rowNumber }: ItemIncidentDetailPanelProps) {
  const { data, isLoading, isError } = useItemIncidentDetail(rowNumber);
  const detail = data?.data;

  return (
    <GlassCard className="mb-6">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {detail ? detail.row.no || `${String(rowNumber)}行目` : `${String(rowNumber)}行目`}{' '}
            の依頼
          </h2>
          {detail && (
            <Badge
              variant={
                detail.row.statusCategory === 'done'
                  ? 'success'
                  : detail.row.statusCategory === 'notStarted'
                    ? 'error'
                    : 'warning'
              }
              size="sm"
            >
              {detail.row.status || '未着手'}
            </Badge>
          )}
        </div>
      </div>

      {isLoading && <div className="skeleton-shimmer h-32 rounded-md" aria-label="読み込み中" />}
      {isError && (
        <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>
          依頼を読み込めませんでした。行番号が正しいか確認してください。
        </p>
      )}

      {detail && (
        <>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MetaItem label="起票日" value={detail.row.requestedOn ?? ''} />
            <MetaItem label="依頼者" value={detail.row.requester} />
            <MetaItem label="サービス" value={detail.row.service} />
            <MetaItem label="ブランド" value={detail.row.brand} />
            <MetaItem label="pattern_id" value={detail.row.patternId} />
            <MetaItem label="SKU" value={detail.row.skuId} />
            <MetaItem label="担当" value={detail.row.checker || '未割当'} />
            <MetaItem label="対応期日" value={detail.row.dueDate} />
          </dl>

          <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-text-muted)' }}>
            {detail.row.kind || '確認内容'}
          </p>
          <p
            className="text-sm whitespace-pre-wrap mb-4"
            style={{ color: 'var(--color-text-primary)' }}
          >
            {detail.row.body}
          </p>

          {detail.row.progress && (
            <>
              <p
                className="text-xs font-semibold mb-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                進捗
              </p>
              <p
                className="text-sm whitespace-pre-wrap mb-4"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {detail.row.progress}
              </p>
            </>
          )}

          {detail.canAnswer ? (
            <AnswerForm detail={detail} />
          ) : (
            <>
              <p
                className="text-xs font-semibold mb-1"
                style={{ color: 'var(--color-text-muted)' }}
              >
                回答
              </p>
              <p
                className="text-sm whitespace-pre-wrap mb-2"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {detail.row.answer || 'まだ回答はありません。'}
              </p>
            </>
          )}
        </>
      )}
    </GlassCard>
  );
}
